// iter 275 round 35 — Container <FotoEditorLightbox>.
//
// Estratto da FotoEditor.jsx come componente self-contained. Riceve tutto via
// props per non richiedere lift di stato (lo state resta nel parent). Render
// il full-screen overlay + top bar + <BeforeAfterImage> + 3 bottoni azione +
// link "Rifai con altre impostazioni" / "Elimina questa foto".
//
// Niente effetti collaterali esterni: tutti gli handler vengono dal parent
// (downloadOne, shareImage, setImportingId, reopenInEditor, setHistoryDeleteConfirm).
//
// iter v30 r094 — User M1750: «la cosa della card e del video la metterei
// anche dentro nella galleria dell'editor foto». Aggiunti due item al
// bottom-sheet "Altre azioni" (Card Prima/Dopo + Video Prima/Dopo) — solo
// per foto live perché serve l'originale per il before/after.

import React, { useState } from "react";
import { toast } from "sonner";
import {
  X, DownloadSimple, Share, House, Trash, ArrowsClockwise, Sparkle,
  DotsThreeOutline, FilmStrip, GridFour,
} from "@phosphor-icons/react";

import { galleryItemUrl, liveFileUrl, liveBeforeAfterCardUrl, liveBeforeAfterVideoUrl } from "@/lib/fotoUrls";
import { shareVideo } from "@/lib/share";
import { BeforeAfterImage } from "@/components/photo/FotoLightboxParts";
import { LiveFeedbackBar } from "@/components/photo/LiveFeedbackBar";

export function FotoEditorLightbox({
  item,                       // foto-history item
  view,                       // "before" | "after"
  setView,                    // setter
  onClose,                    // chiudi lightbox
  properties,                 // lista properties (per "Usa")
  downloadOne,                // (realId) → scarica live preview after
  shareImage,                 // ({url, filename, title, text}) → web share
  setImportingId,             // apre picker "Usa in annuncio…"
  reopenInEditor,             // (item) → ricarica nel form Editor
  onDeleteRequest,            // ({id, label}) → apre conferma elimina
  onCreaContenuti,            // iter v26 r11 — opzionale (item) → adHocSocialModal
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  if (!item) return null;
  const h = item;
  const realId = (h.id || "").replace(/^(live|prop):/, "");
  const isLive = h.source === "live";
  const afterSrc = galleryItemUrl(h);
  const beforeSrc = isLive ? liveFileUrl(realId, "before") : "";
  const imgSrc = isLive && view === "before" ? beforeSrc : afterSrc;
  const kindLabel = h.kind === "stage" ? "Arredata"
    : h.kind === "restyle" ? "Rinnovata"
    : h.kind === "furnish_plan" ? "Pianta arredata"
    : "Migliorata";

  // iter v28 r459 — User M1376: Salva = SOLO download, niente share sheet
  // (era misto: su mobile apriva navigator.share). Share resta sul bottone
  // "Condividi" dedicato. + User M1377: aggiunto "Salva originale" per
  // scaricare il PRIMA (solo se la foto è live = abbiamo l'originale).
  const _downloadBlob = async (src, filename) => {
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (blob.size === 0 || blob.type.startsWith("text/")) {
        throw new Error("Empty blob");
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      return true;
    } catch {
      const a = document.createElement("a");
      a.href = src;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      return false;
    }
  };

  const dlFn = isLive
    ? () => downloadOne(realId, "after")
    : async () => {
        const filename = `pronto-foto-modificata-${realId.slice(0, 8)}.jpg`;
        const ok = await _downloadBlob(imgSrc, filename);
        if (ok) toast.success("Foto scaricata");
        else toast.success("Foto aperta — usa 'Salva con nome'");
      };

  const dlOriginal = isLive && beforeSrc
    ? () => downloadOne(realId, "before")
    : null;

  const onShareClick = () => shareImage({
    url: imgSrc,
    filename: `pronto-foto-${realId.slice(0, 8)}.jpg`,
    title: "",
    text: "Ecco la foto",
  });

  // iter v30 r094 — Card Prima/Dopo + Video Prima/Dopo (solo per foto live).
  // Riusiamo i due endpoint /api/live/before-after-card e -video con cache
  // S3 (iter v30 r093). Stesso flow di GalleriaLightbox: toast loading +
  // share sheet nativo + gestione cancel.
  // iter v30 r095 — Etichetta descrittiva del file nel share sheet.
  const handleShareCard = async () => {
    if (!isLive) return;
    toast.loading("Preparo la card prima/dopo…", { id: "fe-card" });
    try {
      const res = await shareImage({
        url: liveBeforeAfterCardUrl(realId),
        filename: `pronto-prima-dopo-${realId.slice(0, 8)}.jpg`,
        title: "",
        text: "Ecco la card prima e dopo",
      });
      toast.dismiss("fe-card");
      if (res?.cancelled) return;
      if (!res?.shared && !res?.downloaded) {
        toast.error("Card non generata. Riprova fra qualche secondo.", { id: "fe-card" });
      }
    } catch {
      toast.error("Card non generata. Riprova fra qualche secondo.", { id: "fe-card" });
    }
  };

  const handleShareVideo = async () => {
    if (!isLive) return;
    toast.loading("Preparo il video 6s… (può richiedere fino a 1 minuto)", { id: "fe-video" });
    try {
      const res = await shareVideo({
        url: liveBeforeAfterVideoUrl(realId, "slider"),
        filename: `pronto-prima-dopo-${realId.slice(0, 8)}.mp4`,
        title: "",
        text: "Ecco il video prima e dopo",
      });
      toast.dismiss("fe-video");
      if (res?.cancelled) return;
      if (!res?.shared && !res?.downloaded) {
        toast.error("Video non generato. Riprova fra qualche secondo.", { id: "fe-video" });
      }
    } catch {
      toast.error("Video non generato, riprova", { id: "fe-video" });
    }
  };

  const onRimodifica = async () => {
    const alreadyEnhanced = h.kind === "enhance" || !h.kind;
    const msg = alreadyEnhanced
      ? "Questa foto è già migliorata. Vuoi riprovare con altre impostazioni? Userà altri crediti."
      : "Vuoi riprovare questa foto con altre impostazioni? Userà altri crediti.";
    // iter v26 — window.confirm() bloccato silenziosamente su iOS PWA
    // → utente cliccava "Rifai" e non succedeva nulla. Sostituito con toast
    // action button (sonner). iter v26 r5 — label "Rifai" → "Riprova".
    const ok = await new Promise((resolve) => {
      let decided = false;
      const decide = (v) => { if (!decided) { decided = true; resolve(v); } };
      toast.warning(msg, {
        duration: 12000,
        action: { label: "Riprova", onClick: () => decide(true) },
        cancel: { label: "Annulla", onClick: () => decide(false) },
        onDismiss: () => decide(false),
        onAutoClose: () => decide(false),
      });
    });
    if (ok) reopenInEditor(h);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/92 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      data-testid="foto-lightbox"
    >
      {/* FLOATING close X — fixed sopra qualsiasi top bar Safari. */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="fixed top-[max(env(safe-area-inset-top),12px)] right-3 z-[60] w-11 h-11 rounded-full bg-white text-[#1C1C1A] inline-flex items-center justify-center shadow-lg hover:bg-[#F1F4ED] active:scale-95 transition-all"
        aria-label="Chiudi"
        data-testid="foto-lightbox-close"
      >
        <X size={20} weight="bold" />
      </button>

      {/* Top bar */}
      <div
        className="flex items-center px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8A66B]">
            {kindLabel}
          </div>
          {h.property_title && (
            <div className="text-[12.5px] text-white/85 truncate max-w-[55vw]">
              {h.property_title}
            </div>
          )}
        </div>
        {/* iter 275 round 46: feedback bar fine, in linea col mood. Solo live. */}
        <LiveFeedbackBar liveId={realId} isLive={isLive} />
      </div>

      {/* BeforeAfter image (deduped tra FotoEditor + GalleriaFoto) */}
      <BeforeAfterImage
        afterSrc={afterSrc}
        beforeSrc={beforeSrc}
        isLive={isLive}
        kindLabel={kindLabel}
        lightboxView={view}
        setLightboxView={setView}
        onClose={onClose}
        testidPrefix="foto-lightbox"
      />

      {/* Bottom actions · IDENTICO pattern di GalleriaLightbox (iter v30 r094):
          4 bottoni primary sempre visibili + bottom-sheet "Altre azioni" con
          le secondarie (Originale, Card Prima/Dopo, Video Prima/Dopo,
          Contenuti, Usa, Elimina). */}
      <div
        className="px-3 pb-[max(env(safe-area-inset-bottom),16px)] pt-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#1C1C1A] rounded-2xl shadow-2xl p-2 max-w-2xl mx-auto">
          <div className="grid grid-cols-4 gap-1.5">
            <button
              onClick={onRimodifica}
              className="bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 transition-colors leading-tight"
              data-testid="foto-lightbox-rimodifica"
              title="Apri nell'Editor per riprovare con altre impostazioni"
            >
              <ArrowsClockwise size={13} weight="bold" className="shrink-0" />
              <span>Riprova</span>
            </button>
            <button
              onClick={dlFn}
              className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
              data-testid="foto-lightbox-dl"
              title="Scarica la foto modificata"
            >
              <DownloadSimple size={13} weight="bold" className="shrink-0" />
              <span>Salva</span>
            </button>
            <button
              onClick={onShareClick}
              className="bg-white text-[#1C1C1A] py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 hover:bg-[#F1F4ED] transition-colors leading-tight"
              data-testid="foto-lightbox-share"
              title="Apri condivisione (WhatsApp, Email, AirDrop…)"
            >
              <Share size={13} weight="bold" className="shrink-0" />
              <span>Condividi</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setMoreOpen(true); }}
              className="bg-[#C8A66B] hover:bg-[#B89656] text-white py-2.5 px-1.5 rounded-full text-[10.5px] font-semibold inline-flex items-center justify-center gap-1 transition-colors leading-tight"
              data-testid="foto-lightbox-more"
              title="Altre azioni · card, video prima/dopo, originale, elimina"
              aria-label="Più azioni"
            >
              <DotsThreeOutline size={16} weight="bold" className="shrink-0" />
            </button>
          </div>
        </div>
      </div>

      {/* iter v30 r094 — Bottom sheet "Altre azioni" con secondarie:
            - Card Prima/Dopo (solo live)
            - Video Prima/Dopo (solo live)
            - Salva originale (solo live)
            - Contenuti (se onCreaContenuti)
            - Usa in annuncio / Apri annuncio
            - Elimina */}
      {moreOpen && (
        <div
          className="absolute inset-0 z-[60] bg-black/55 flex items-end sm:items-center justify-center"
          onClick={(e) => { e.stopPropagation(); setMoreOpen(false); }}
          data-testid="foto-more-sheet"
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-[#EAE7DF] flex items-center justify-between">
              <div className="font-editorial text-lg text-[#1C1C1A]">Altre azioni</div>
              <button
                onClick={() => setMoreOpen(false)}
                className="w-8 h-8 rounded-full bg-[#F1F4ED] hover:bg-[#E5E7E0] inline-flex items-center justify-center transition-colors"
                aria-label="Chiudi"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-3 py-3 space-y-1.5">
              {isLive && (
                <>
                  <SheetItem
                    icon={GridFour}
                    title="Card Prima e Dopo"
                    desc="Immagine editoriale 1080×1350 per IG/WhatsApp"
                    onClick={() => { setMoreOpen(false); handleShareCard(); }}
                    testid="foto-more-card"
                  />
                  <SheetItem
                    icon={FilmStrip}
                    title="Video Prima/Dopo · 8 sec"
                    desc="MP4 con transizione automatica, ottimo per reel"
                    onClick={() => { setMoreOpen(false); handleShareVideo(); }}
                    testid="foto-more-video"
                  />
                </>
              )}
              {dlOriginal && (
                <SheetItem
                  icon={DownloadSimple}
                  title="Salva foto originale"
                  desc="Scarica la foto PRIMA della modifica (.jpg)"
                  onClick={() => { setMoreOpen(false); dlOriginal(); }}
                  testid="foto-more-dl-original"
                />
              )}
              {onCreaContenuti && (
                <SheetItem
                  icon={Sparkle}
                  title="Crea contenuti"
                  desc="Post social, PDF stampabile o volantino con questa foto"
                  onClick={() => { setMoreOpen(false); onClose(); onCreaContenuti(h); }}
                  testid="foto-more-contenuti"
                />
              )}
              {isLive && properties.length > 0 && (
                <SheetItem
                  icon={House}
                  title="Usa in un annuncio"
                  desc="Importa questa foto in un immobile esistente"
                  onClick={() => { setMoreOpen(false); onClose(); setImportingId(h.id); }}
                  testid="foto-more-use"
                />
              )}
              {!isLive && h.property_id && (
                <a
                  href={`/dashboard/property/${h.property_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-[#FAF8F3] transition-colors"
                  data-testid="foto-more-open-property"
                >
                  <span className="shrink-0 w-9 h-9 rounded-full bg-[#F1F4ED] inline-flex items-center justify-center text-[#7C8B6E]">
                    <House size={16} weight="fill" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] text-[#1C1C1A]">Apri annuncio</div>
                    <div className="text-[11.5px] text-[#6B6A65]">Vai alla scheda dell&apos;immobile</div>
                  </div>
                </a>
              )}
              <SheetItem
                icon={Trash}
                title="Elimina foto"
                desc="Rimuove la foto dalla galleria (non reversibile)"
                danger
                onClick={() => {
                  setMoreOpen(false);
                  onDeleteRequest({ id: h.id, label: kindLabel });
                }}
                testid="foto-more-delete"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SheetItem({ icon: Icon, title, desc, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3 rounded-xl hover:bg-[#FAF8F3] transition-colors text-left"
      data-testid={testid}
    >
      <span className={`shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center ${
        danger ? "bg-red-100 text-red-600" : "bg-[#F1F4ED] text-[#7C8B6E]"
      }`}>
        <Icon size={16} weight="fill" />
      </span>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-[13px] ${danger ? "text-red-600" : "text-[#1C1C1A]"}`}>{title}</div>
        <div className="text-[11.5px] text-[#6B6A65] leading-snug">{desc}</div>
      </div>
    </button>
  );
}
